import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from "firebase/firestore";
import {
  getDownloadURL,
  ref,
  uploadBytes
} from "firebase/storage";

import { db, storage } from "../firebase";

export const GLOBAL_CONVERSATION_ID = "tum_kullanicilar";

export const getConversationId = (firstUid, secondUid) =>
  [firstUid, secondUid].sort().join("_");

const timestampToMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
};

const getUserSummary = (user) => ({
  uid: user?.uid || user?.id || "",
  name: user?.name || user?.email || "Kullanıcı",
  email: user?.email || "",
  role: user?.role || "viewer",
  photoURL: user?.photoURL || ""
});

export const subscribeUserConversations = (uid, callback) => {
  if (!uid) return () => {};

  const conversationsQuery = query(
    collection(db, "conversations"),
    where("participants", "array-contains", uid)
  );

  return onSnapshot(conversationsQuery, (snapshot) => {
    callback(
      snapshot.docs
        .map((conversationDoc) => ({
          id: conversationDoc.id,
          ...conversationDoc.data()
        }))
        .sort(
          (first, second) =>
            timestampToMillis(second.updatedAt) - timestampToMillis(first.updatedAt)
        )
    );
  });
};

export const subscribeUnreadConversations = (uid, callback) => {
  const unsubscribers = [];
  let directUnread = [];
  let globalUnread = [];

  const emitUnread = () => {
    callback([...directUnread, ...globalUnread]);
  };

  unsubscribers.push(subscribeUserConversations(uid, (conversations) => {
    directUnread = conversations.filter((conversation) =>
      Array.isArray(conversation.unreadBy) && conversation.unreadBy.includes(uid)
    );
    emitUnread();
  }));

  unsubscribers.push(subscribeGlobalConversation((conversation) => {
    const readBy = Array.isArray(conversation?.readBy)
      ? conversation.readBy
      : [];
    const isUnread =
      Boolean(conversation?.lastMessage) &&
      conversation.lastSenderId !== uid &&
      !readBy.includes(uid);

    globalUnread = isUnread ? [conversation] : [];
    emitUnread();
  }));

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe?.());
  };
};

export const subscribeGlobalConversation = (callback) => {
  return onSnapshot(doc(db, "conversations", GLOBAL_CONVERSATION_ID), (snapshot) => {
    callback(
      snapshot.exists()
        ? {
            id: snapshot.id,
            ...snapshot.data()
          }
        : null
    );
  });
};

export const subscribeConversationMessages = (conversationId, callback) => {
  if (!conversationId) return () => {};

  const messagesQuery = query(
    collection(db, "conversations", conversationId, "messages"),
    orderBy("createdAt", "asc")
  );

  return onSnapshot(messagesQuery, (snapshot) => {
    callback(
      snapshot.docs.map((messageDoc) => ({
        id: messageDoc.id,
        ...messageDoc.data()
      }))
    );
  });
};

export const sendMessage = async (payload) => {
  return sendDirectMessage(payload);
};

export const sendDirectMessage = async ({
  currentUser,
  targetUser,
  text,
  attachments = [],
  replyTo = null,
  forwardedFrom = null
}) => {
  const cleanText = String(text || "").trim();
  const cleanAttachments = sanitizeAttachments(attachments);
  const currentUserSummary = getUserSummary(currentUser);
  const targetUserSummary = getUserSummary(targetUser);

  if ((!cleanText && cleanAttachments.length === 0) || !currentUserSummary.uid || !targetUserSummary.uid) return;

  const conversationId = getConversationId(
    currentUserSummary.uid,
    targetUserSummary.uid
  );
  const conversationRef = doc(db, "conversations", conversationId);

  await setDoc(
    conversationRef,
    {
      participants: [currentUserSummary.uid, targetUserSummary.uid].sort(),
      participantInfo: {
        [currentUserSummary.uid]: currentUserSummary,
        [targetUserSummary.uid]: targetUserSummary
      },
      lastMessage: getLastMessagePreview(cleanText, cleanAttachments),
      lastMessageAt: serverTimestamp(),
      lastSenderId: currentUserSummary.uid,
      unreadBy: [targetUserSummary.uid],
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  const messagePayload = {
    senderId: currentUserSummary.uid,
    senderName: currentUserSummary.name,
    senderEmail: currentUserSummary.email,
    text: cleanText,
    attachments: cleanAttachments,
    createdAt: serverTimestamp(),
    readBy: [currentUserSummary.uid]
  };

  if (replyTo) {
    messagePayload.replyTo = sanitizeMessageReference(replyTo);
  }

  if (forwardedFrom) {
    messagePayload.forwardedFrom = sanitizeMessageReference(forwardedFrom);
  }

  await addDoc(collection(conversationRef, "messages"), messagePayload);
};

export const sendGlobalMessage = async ({
  currentUser,
  text,
  attachments = [],
  replyTo = null,
  forwardedFrom = null
}) => {
  const cleanText = String(text || "").trim();
  const cleanAttachments = sanitizeAttachments(attachments);
  const currentUserSummary = getUserSummary(currentUser);

  if ((!cleanText && cleanAttachments.length === 0) || !currentUserSummary.uid) return;

  const conversationRef = doc(db, "conversations", GLOBAL_CONVERSATION_ID);

  await setDoc(
    conversationRef,
    {
      type: "global",
      title: "Tüm Kullanıcılar",
      lastMessage: getLastMessagePreview(cleanText, cleanAttachments),
      lastMessageAt: serverTimestamp(),
      lastSenderId: currentUserSummary.uid,
      readBy: [currentUserSummary.uid],
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  const messagePayload = {
    senderId: currentUserSummary.uid,
    senderName: currentUserSummary.name,
    senderEmail: currentUserSummary.email,
    text: cleanText,
    attachments: cleanAttachments,
    createdAt: serverTimestamp(),
    readBy: [currentUserSummary.uid]
  };

  if (replyTo) {
    messagePayload.replyTo = sanitizeMessageReference(replyTo);
  }

  if (forwardedFrom) {
    messagePayload.forwardedFrom = sanitizeMessageReference(forwardedFrom);
  }

  await addDoc(collection(conversationRef, "messages"), messagePayload);
};

export const toggleMessageReaction = async ({
  conversationId,
  message,
  emoji,
  currentUser
}) => {
  const uid = currentUser?.uid || currentUser?.id || "";

  if (!conversationId || !message?.id || !uid || !emoji) return;

  const reactions = { ...(message.reactions || {}) };
  const currentReactionUsers = Array.isArray(reactions[emoji])
    ? reactions[emoji]
    : [];
  const nextReactionUsers = currentReactionUsers.includes(uid)
    ? currentReactionUsers.filter((item) => item !== uid)
    : [...currentReactionUsers, uid];

  if (nextReactionUsers.length) {
    reactions[emoji] = nextReactionUsers;
  } else {
    delete reactions[emoji];
  }

  await updateDoc(doc(db, "conversations", conversationId, "messages", message.id), {
    reactions,
    updatedAt: serverTimestamp()
  });
};

export const markConversationMessagesRead = async ({
  conversationId,
  messages = [],
  uid
}) => {
  if (!conversationId || !uid || !Array.isArray(messages) || messages.length === 0) return;

  const unreadMessages = messages.filter((message) =>
    message?.id &&
    message.senderId !== uid &&
    !(Array.isArray(message.readBy) && message.readBy.includes(uid))
  );

  if (unreadMessages.length === 0) return;

  const batch = writeBatch(db);

  unreadMessages.slice(0, 100).forEach((message) => {
    batch.update(doc(db, "conversations", conversationId, "messages", message.id), {
      readBy: arrayUnion(uid),
      updatedAt: serverTimestamp()
    });
  });

  await batch.commit();
};

const sanitizeMessageReference = (message) => ({
  id: String(message?.id || "").slice(0, 120),
  text: String(message?.text || getLastMessagePreview("", message?.attachments || []) || "").slice(0, 240),
  senderId: String(message?.senderId || "").slice(0, 120),
  senderName: String(message?.senderName || message?.name || "Kullanıcı").slice(0, 120),
  senderEmail: String(message?.senderEmail || message?.email || "").slice(0, 160)
});

const sanitizeAttachments = (attachments = []) =>
  attachments
    .filter(Boolean)
    .slice(0, 5)
    .map((attachment) => ({
      name: String(attachment.name || "Dosya").slice(0, 180),
      type: String(attachment.type || "application/octet-stream").slice(0, 120),
      size: Number(attachment.size || 0),
      url: String(attachment.url || ""),
      path: String(attachment.path || ""),
      isImage: Boolean(attachment.isImage || String(attachment.type || "").startsWith("image/"))
    }))
    .filter((attachment) => attachment.url);

const getLastMessagePreview = (text, attachments = []) => {
  const cleanText = String(text || "").trim();

  if (cleanText) return cleanText.slice(0, 180);

  const cleanAttachments = sanitizeAttachments(attachments);
  if (cleanAttachments.length === 0) return "";
  if (cleanAttachments.length === 1) {
    return cleanAttachments[0].isImage ? "📷 Fotoğraf" : `📎 ${cleanAttachments[0].name}`;
  }

  return `📎 ${cleanAttachments.length} ek`;
};

export const uploadChatAttachment = async ({
  conversationId,
  currentUser,
  file
}) => {
  const uid = currentUser?.uid || currentUser?.id || "";

  if (!conversationId || !uid || !file) {
    throw new Error("Dosya yükleme bilgileri eksik.");
  }

  const safeName = String(file.name || "dosya")
    .replace(/[^\p{L}\p{N}._-]+/gu, "-")
    .slice(0, 120);
  const filePath = `chatAttachments/${conversationId}/${Date.now()}-${uid}-${safeName}`;
  const fileRef = ref(storage, filePath);

  await uploadBytes(fileRef, file, {
    contentType: file.type || "application/octet-stream"
  });

  const url = await getDownloadURL(fileRef);

  return {
    name: file.name || "Dosya",
    type: file.type || "application/octet-stream",
    size: file.size || 0,
    url,
    path: filePath,
    isImage: String(file.type || "").startsWith("image/")
  };
};

export const markConversationRead = async ({ conversation, uid }) => {
  if (!conversation?.id || !uid) return;

  if (conversation.type === "global") {
    await updateDoc(doc(db, "conversations", conversation.id), {
      readBy: arrayUnion(uid),
      updatedAt: serverTimestamp()
    });

    return;
  }

  const unreadBy = Array.isArray(conversation.unreadBy)
    ? conversation.unreadBy.filter((item) => item !== uid)
    : [];

  await updateDoc(doc(db, "conversations", conversation.id), {
    unreadBy,
    updatedAt: serverTimestamp()
  });
};

export const clearConversationMessages = async ({
  conversationId,
  currentUser
}) => {
  const uid = currentUser?.uid || currentUser?.id || "";

  if (!conversationId || !uid) {
    throw new Error("Sohbet temizleme bilgileri eksik.");
  }

  const conversationRef = doc(db, "conversations", conversationId);
  const messagesSnapshot = await getDocs(
    query(collection(conversationRef, "messages"), orderBy("createdAt", "asc"))
  );

  let batch = writeBatch(db);
  let batchOperationCount = 0;

  for (const messageDoc of messagesSnapshot.docs) {
    batch.delete(messageDoc.ref);
    batchOperationCount += 1;

    if (batchOperationCount >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      batchOperationCount = 0;
    }
  }

  if (batchOperationCount > 0) {
    await batch.commit();
  }

  await updateDoc(conversationRef, {
    lastMessage: "",
    lastMessageAt: null,
    lastSenderId: "",
    unreadBy: [],
    readBy: [uid],
    clearedAt: serverTimestamp(),
    clearedBy: uid,
    clearedByEmail: currentUser?.email || "",
    updatedAt: serverTimestamp()
  });
};
